import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import {
  FIELD_TYPE_META, FIELD_TYPE_OPTIONS, ASSET_LIFECYCLE_META, ASSET_LIFECYCLE_OPTIONS,
} from './assetShared';

// Demirbaş Özellik Tanımı — demirbaş modellerine takılabilen özellik (attribute) sözlüğü.
// NOTE: /api/asset-attribute-defs REST (OData değil) — ApiResponse<{ items }>. useC360List
// kullanılamaz; veri tek seferde (take=500) çekilir, filtre/sıralama/sayfalama c360 arayüzü
// ile istemci tarafında uygulanır. CRUD REST (POST / PUT/{id} / DELETE/{id}).
const API_BASE = '/api/asset-attribute-defs';
const PAGE_SIZE = 20;

const COLUMN_FILTER_META = {
  name: { type: 'text', odataField: 'Name' },
  fieldType: { type: 'select', odataField: 'FieldType', options: FIELD_TYPE_OPTIONS },
  lifecycleStatus: { type: 'select', odataField: 'LifecycleStatus', options: ASSET_LIFECYCLE_OPTIONS },
};
const SEARCH_FIELDS = ['name', 'textId', 'unit'];

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return { items: pick(data, 'items', 'Items') ?? [] };
}

// optionsJson (JSON dizi string'i) → adet gösterimi.
function optionCount(raw) {
  if (!raw) return 0;
  try {
    const parsed = Array.isArray(raw) ? raw : JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch { return 0; }
}

function boolPill(v) {
  return v
    ? <StatusBadge tone="ok">Evet</StatusBadge>
    : <span style={{ color: '#94a3b8' }}>Hayır</span>;
}

export function AssetAttributeDefsPage() {
  usePageMeta('Demirbaş Özellik Tanımı', 'Demirbaş modellerine takılabilen özellik (attribute) sözlüğü.');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [allRows, setAllRows] = useState([]);
  // Yusuf Fix — LİSTE AÇILIŞINDA "Kayıt bulunamadı" YANIP SÖNMESİN: veri mount'taki effect'te
  // çekiliyor, yani ilk boyama fetch'ten ÖNCE olur. loading false başlayınca tablo o ilk karede
  // boş-durum metnini gösteriyor, veri gelince listeye dönüyordu — kullanıcı "önce kayıt yok dedi,
  // sonra liste geldi" diye okuyor. Yükleme durumu ilk kareden itibaren AÇIK başlar.
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [selected, setSelected] = useState(null);

  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [sortField, setSortField] = useState('UpdatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('take', '500');
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setAllRows(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const counts = useMemo(() => ({
    total: allRows.length,
    active: allRows.filter((r) => Number(pick(r, 'lifecycleStatus', 'LifecycleStatus')) === 1).length,
    required: allRows.filter((r) => !!pick(r, 'isRequired', 'IsRequired')).length,
  }), [allRows]);

  const filteredSorted = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    const matchSearch = (row) => {
      if (!needle) return true;
      return SEARCH_FIELDS.some((f) => {
        const v = pick(row, f, f.charAt(0).toUpperCase() + f.slice(1));
        return v && String(v).toLocaleLowerCase('tr-TR').includes(needle);
      });
    };
    const matchColumns = (row) => Object.entries(columnFilters).every(([colId, filter]) => {
      if (!filter) return true;
      const meta = COLUMN_FILTER_META[colId];
      const cap = colId.charAt(0).toUpperCase() + colId.slice(1);
      const raw = pick(row, colId, cap);
      if (meta?.type === 'select') {
        if (filter.value === '' || filter.value == null) return true;
        return String(raw) === String(filter.value);
      }
      const fv = (filter.value ?? '').toString().trim().toLocaleLowerCase('tr-TR');
      if (!fv) return true;
      return (raw ?? '').toString().toLocaleLowerCase('tr-TR').includes(fv);
    });

    const filtered = allRows.filter((r) => matchSearch(r) && matchColumns(r));
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'Name': return pick(row, 'name', 'Name') || '';
        case 'FieldType': return pick(row, 'fieldType', 'FieldType') || '';
        case 'LifecycleStatus': return Number(pick(row, 'lifecycleStatus', 'LifecycleStatus') ?? 0);
        case 'UpdatedDate':
        default: {
          const v = pick(row, 'updatedDate', 'UpdatedDate');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [allRows, filterText, columnFilters, sortField, sortDir]);

  const totalCount = filteredSorted.length;
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, currentPage, pageSize]);

  const applyFilterText = useCallback((text) => { setFilterText(text); setCurrentPage(1); }, []);
  const setColumnFilter = useCallback((colId, next) => { setColumnFilters((p) => ({ ...p, [colId]: next })); setCurrentPage(1); }, []);
  const clearColumnFilter = useCallback((colId) => {
    setColumnFilters((p) => { const c = { ...p }; delete c[colId]; return c; });
    setCurrentPage(1);
  }, []);

  const openNew = () => navigate('/asset-attribute-defs/new');
  const openDetail = (row = selected) => { if (!row) return; navigate(`/asset-attribute-defs/${pick(row, 'id', 'Id')}`, { state: { row } }); };
  const openEditRow = (row = selected) => { if (!row) return; navigate(`/asset-attribute-defs/${pick(row, 'id', 'Id')}?edit=1`, { state: { row } }); };

  const handleDelete = async () => {
    if (!selected) return;
    const id = pick(selected, 'id', 'Id');
    setDeleteBusy(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `Silme başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', 'Özellik tanımı silindi.');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setDeleteConfirm(false);
    } finally {
      setDeleteBusy(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      header: 'Özellik Adı', meta: { filterType: 'text' },
      cell: ({ row }) => {
        const t = pick(row.original, 'name', 'Name');
        const tid = pick(row.original, 'textId', 'TextId');
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
            <span style={{ fontWeight: 600 }}>{t || '-'}</span>
            {tid && <span style={{ fontSize: 11, color: 'var(--text-secondary, #6b7280)' }}>{tid}</span>}
          </div>
        );
      },
    }),
    columnHelper.accessor('fieldType', {
      header: 'Alan Tipi', meta: { filterType: 'select' },
      cell: ({ row }) => {
        const t = pick(row.original, 'fieldType', 'FieldType');
        const meta = FIELD_TYPE_META[t] || { label: t || '-', tone: 'neutral' };
        return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
      },
    }),
    columnHelper.accessor('unit', {
      header: 'Birim', enableColumnFilter: false,
      cell: ({ row }) => pick(row.original, 'unit', 'Unit') || '-',
    }),
    columnHelper.accessor('isRequired', {
      header: 'Zorunlu', enableColumnFilter: false,
      cell: ({ row }) => boolPill(pick(row.original, 'isRequired', 'IsRequired')),
    }),
    columnHelper.display({
      id: 'options', header: 'Seçenek',
      cell: ({ row }) => {
        const n = optionCount(pick(row.original, 'optionsJson', 'OptionsJson'));
        return n > 0 ? <StatusBadge tone="neutral">{n} seçenek</StatusBadge> : <span style={{ color: '#94a3b8' }}>-</span>;
      },
    }),
    columnHelper.accessor('lifecycleStatus', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: ({ row }) => {
        const st = Number(pick(row.original, 'lifecycleStatus', 'LifecycleStatus') ?? 0);
        const meta = ASSET_LIFECYCLE_META[st] || { label: '-', tone: 'neutral' };
        return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
      },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const gridToOData = {
    name: 'Name',
    fieldType: 'FieldType',
    lifecycleStatus: 'LifecycleStatus',
  };
  const odataToGrid = Object.fromEntries(Object.entries(gridToOData).map(([k, v]) => [v, k]));
  const sorting = useMemo(() => {
    const gridId = odataToGrid[sortField];
    return gridId ? [{ id: gridId, desc: sortDir === 'desc' }] : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    setCurrentPage(1);
    if (!first) { setSortField('UpdatedDate'); setSortDir('desc'); return; }
    setSortField(gridToOData[first.id] || 'UpdatedDate');
    setSortDir(first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: pagedRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(pick(row, 'id', 'Id')),
  });

  const handleExport = () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'name', headerName: 'Özellik Adı' },
        { field: 'textId', headerName: 'Kod' },
        { headerName: 'Alan Tipi', valueGetter: ({ row }) => (FIELD_TYPE_META[pick(row, 'fieldType', 'FieldType')]?.label || pick(row, 'fieldType', 'FieldType') || '') },
        { field: 'unit', headerName: 'Birim' },
        { headerName: 'Zorunlu', valueGetter: ({ row }) => (pick(row, 'isRequired', 'IsRequired') ? 'Evet' : 'Hayır') },
        { headerName: 'Seçenek Adedi', valueGetter: ({ row }) => optionCount(pick(row, 'optionsJson', 'OptionsJson')) },
        { headerName: 'Durum', valueGetter: ({ row }) => (ASSET_LIFECYCLE_META[Number(pick(row, 'lifecycleStatus', 'LifecycleStatus') ?? 0)]?.label || '') },
      ];
      if (filteredSorted.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(filteredSorted, cols, `demirbas-ozellikler_${dateStr}`);
      showToast('success', `${filteredSorted.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Özellik adı / kod / birim ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={pagedRows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Demirbaş Özellik Tanımı"
        subtitle="Demirbaş modellerine takılabilen özellik (attribute) sözlüğü. Seçim listesi, sayısal aralık ve barkod tipleri burada tanımlanır."
        actions={canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Özellik</button>}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '4px 0 10px' }}>
          <StatusBadge tone="neutral">Toplam: {counts.total}</StatusBadge>
          <StatusBadge tone="ok">Aktif: {counts.active}</StatusBadge>
          <StatusBadge tone="neutral">Zorunlu: {counts.required}</StatusBadge>
        </div>
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && selected && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Özellik Tanımı Sil</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>"{pick(selected, 'name', 'Name')}"</strong> özellik tanımını silmek istediğinize emin misiniz?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={{ ...s.btnDanger, opacity: deleteBusy ? 0.6 : 1 }} onClick={handleDelete} disabled={deleteBusy}>{deleteBusy ? 'Siliniyor...' : 'Evet, Sil'}</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)} disabled={deleteBusy}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default AssetAttributeDefsPage;
